import React, { useRef, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import About from './pages/About';
import Products from './pages/Products';
import Clients from './pages/Clients';
import ContractVehicles from './pages/ContractVehicles';
import Opportunities from './pages/Opportunities';
import Contact from './pages/Contact';
import SearchResults from './pages/SearchResults';
import News from './pages/News';
import Admin from './pages/Admin';
import NewsArticle from './pages/NewsArticle';
import WhitepaperDownload from './pages/WhitepaperDownload';
import './App.css';

function App() {
  const mainLogoRef = useRef(null);
  const navbarLogoRef = useRef(null);
  const footerLeftRef = useRef(null);
  const badgeContainerRef = useRef(null);
  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1200);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1200);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (!mainLogoRef.current || !navbarLogoRef.current) return;
      const rect = mainLogoRef.current.getBoundingClientRect();
      if (rect.bottom <= 0) {
        navbarLogoRef.current.classList.add('visible');
      } else {
        navbarLogoRef.current.classList.remove('visible');
      }
    };
    if (!isMobile) {
      window.addEventListener('scroll', handleScroll);
      handleScroll();
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [isMobile]);

  useEffect(() => {
    const setFooterLeftHeight = () => {
      if (footerLeftRef.current && badgeContainerRef.current) {
        const badgeHeight = badgeContainerRef.current.offsetHeight;
        footerLeftRef.current.style.height = badgeHeight + 'px';
      }
    };
    setFooterLeftHeight();
    window.addEventListener('resize', setFooterLeftHeight);
    return () => window.removeEventListener('resize', setFooterLeftHeight);
  }, []);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?s=${encodeURIComponent(searchTerm)}`);
      setShowSearch(false);
      setSearchTerm('');
    }
  };

  // Mobile Navbar
  const mobileNavbar = (
    <nav className="mobile-navbar" aria-label="Main Navigation">
      <div className="mobile-navbar-inner">
        <Link to="/" className="mobile-navbar-logo" tabIndex={0} aria-label="Inalab Home">
          <img src={process.env.PUBLIC_URL + '/Inalab Group Logo - Vector copy.png'} alt="Inalab Logo" height={32} />
        </Link>
        <button
          className="mobile-hamburger"
          aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-controls="mobile-nav-menu"
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen((open) => !open)}
        >
          <span className="hamburger-bar" />
          <span className="hamburger-bar" />
          <span className="hamburger-bar" />
        </button>
      </div>
      <div
        id="mobile-nav-menu"
        className={`mobile-nav-menu${mobileMenuOpen ? ' mobile-nav-open' : ''}`}
        role="menu"
        aria-hidden={!mobileMenuOpen}
      >
        <ul className="mobile-nav-links">
          <li><Link to="/" onClick={() => setMobileMenuOpen(false)}>Home</Link></li>
          <li><Link to="/about" onClick={() => setMobileMenuOpen(false)}>About Us</Link></li>
          <li><Link to="/products" onClick={() => setMobileMenuOpen(false)}>Products</Link></li>
          <li><Link to="/clients" onClick={() => setMobileMenuOpen(false)}>Clients</Link></li>
          <li><Link to="/contract-vehicles" onClick={() => setMobileMenuOpen(false)}>Contract Vehicles</Link></li>
          <li><Link to="/opportunities" onClick={() => setMobileMenuOpen(false)}>Join our Team</Link></li>
          <li><Link to="/news" onClick={() => setMobileMenuOpen(false)}>News</Link></li>
          <li><Link to="/contact" onClick={() => setMobileMenuOpen(false)}>Contact Us</Link></li>
          <li>
            <button className="search-icon-btn" onClick={() => { setShowSearch(true); setMobileMenuOpen(false); }} aria-label="Search">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="7.5" stroke="#222" strokeWidth="2" />
                <line x1="16" y1="16" x2="20" y2="20" stroke="#222" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </li>
        </ul>
      </div>
      {showSearch && (
        <div className="search-modal-overlay" onClick={() => setShowSearch(false)}>
          <div className="search-modal" onClick={e => e.stopPropagation()}>
            <form onSubmit={handleSearchSubmit}>
              <input
                type="text"
                placeholder="Type here"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                autoFocus
              />
              <button type="submit">Search</button>
              <button type="button" onClick={() => setShowSearch(false)}>Close</button>
            </form>
          </div>
        </div>
      )}
    </nav>
  );

  // Desktop Navbar
  const desktopNavbar = (
    <>
      <header className="main-header">
        <Link to="/">
          <img ref={mainLogoRef} src={process.env.PUBLIC_URL + '/Inalab Group Logo - Vector copy.png'} alt="Inalab Logo" className="main-logo" />
        </Link>
      </header>
      <nav className="navbar centered-navbar" aria-label="Main Navigation">
        <img
          ref={navbarLogoRef}
          src={process.env.PUBLIC_URL + '/Inalab Group Logo - Vector copy.png'}
          alt="Inalab Logo Small"
          className="navbar-logo-left"
          height={28}
        />
        <ul className="nav-links">
          <li><Link to="/">Home</Link></li>
          <li><Link to="/about">About Us</Link></li>
          <li><Link to="/products">Products</Link></li>
          <li><Link to="/clients">Clients</Link></li>
          <li><Link to="/contract-vehicles">Contract Vehicles</Link></li>
          <li><Link to="/opportunities">Join our Team</Link></li>
          <li><Link to="/news">News</Link></li>
          <li><Link to="/contact">Contact Us</Link></li>
          <li>
            <button className="search-icon-btn" onClick={() => setShowSearch(true)} aria-label="Search">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" style={{display: 'block'}}>
                <circle cx="10" cy="10" r="7.5" stroke="#222" strokeWidth="2" />
                <line x1="16" y1="16" x2="20" y2="20" stroke="#222" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </li>
        </ul>
        {showSearch && (
          <div className="search-modal-overlay" onClick={() => setShowSearch(false)}>
            <div className="search-modal" onClick={e => e.stopPropagation()}>
              <form onSubmit={handleSearchSubmit}>
                <input
                  type="text"
                  placeholder="Type here"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  autoFocus
                />
                <button type="submit">Search</button>
                <button type="button" onClick={() => setShowSearch(false)}>Close</button>
              </form>
            </div>
          </div>
        )}
      </nav>
    </>
  );

  return (
    <div className="App">
      {isMobile ? mobileNavbar : desktopNavbar}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/products" element={<Products />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/contract-vehicles" element={<ContractVehicles />} />
        <Route path="/opportunities" element={<Opportunities />} />
        <Route path="/news" element={<News />} />
        <Route path="/news/:id" element={<NewsArticle />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/search" element={<React.Suspense fallback={null}><SearchResults /></React.Suspense>} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/whitepaper/:uuid" element={<WhitepaperDownload />} />
      </Routes>
      {isMobile ? (
        <footer className="footer custom-footer mobile-footer" aria-label="Site Footer">
          <div className="mobile-footer-content">
            <div className="mobile-footer-contact-details">
              <div className="footer-contact-row address-row">
                <div className="address-label-row">
                  <span className="footer-contact-icon" aria-label="address">
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 21s-6-5.686-6-10A6 6 0 0 1 18 11c0 4.314-6 10-6 10Z"/><circle cx="12" cy="11" r="2.5"/></svg>
                  </span>
                  <span><b>Address:</b></span>
                </div>
                <div className="address-lines">
                  3949 Pender Dr,<br />
                  Suite 150, Fairfax, VA 22030
                </div>
              </div>
              <div className="footer-contact-row">
                <span className="footer-contact-icon" aria-label="phone">
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.72 19.72 0 0 1 3.08 5.18 2 2 0 0 1 5 3h3a2 2 0 0 1 2 1.72c.13 1.13.37 2.22.72 3.26a2 2 0 0 1-.45 2.11l-1.27 1.27a16 16 0 0 0 6.29 6.29l1.27-1.27a2 2 0 0 1 2.11-.45c1.04.35 2.13.59 3.26.72A2 2 0 0 1 22 16.92Z"/></svg>
                </span>
                <span><b>Phone:</b> (703) 459-1079</span>
              </div>
              <div className="footer-contact-row">
                <span className="footer-contact-icon" aria-label="email">
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 6-10 7L2 6"/></svg>
                </span>
                <span><b>Email:</b> <a href="mailto:sales@inalabgroup.com" className="footer-contact-link">sales@inalabgroup.com</a></span>
              </div>
              <div className="footer-contact-row">
                <a href="/admin" className="footer-link" style={{ display: 'block', textAlign: 'left' }}>Staff Login</a>
              </div>
            </div>
            <div className="mobile-footer-badges">
              <div className="footer-badge-container" ref={badgeContainerRef}>
                <img src={process.env.PUBLIC_URL + '/cert_footer.png'} alt="Certifications and Badges" style={{ width: '100%', maxWidth: '1000px', height: 'auto', display: 'block', marginLeft: 'auto' }} />
              </div>
            </div>
            <div className="footer-copyright" style={{ marginTop: '1.2rem', textAlign: 'center' }}>© {new Date().getFullYear()} Inalab Inc. All rights reserved.</div>
          </div>
        </footer>
      ) : (
        <footer className="footer custom-footer">
          <div className="footer-content">
            <div
              className="footer-left"
              ref={footerLeftRef}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
            >
              <div className="footer-contact-details">
                <div className="footer-contact-row">
                  <span className="footer-contact-icon" aria-label="address">{/* location icon */}
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 21s-6-5.686-6-10A6 6 0 0 1 18 11c0 4.314-6 10-6 10Z"/><circle cx="12" cy="11" r="2.5"/></svg>
                  </span>
                  <span><b>Address:</b> 3949 Pender Dr, Suite 150, Fairfax, VA 22030</span>
                </div>
                <div className="footer-contact-row">
                  <span className="footer-contact-icon" aria-label="phone">{/* phone icon */}
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.72 19.72 0 0 1 3.08 5.18 2 2 0 0 1 5 3h3a2 2 0 0 1 2 1.72c.13 1.13.37 2.22.72 3.26a2 2 0 0 1-.45 2.11l-1.27 1.27a16 16 0 0 0 6.29 6.29l1.27-1.27a2 2 0 0 1 2.11-.45c1.04.35 2.13.59 3.26.72A2 2 0 0 1 22 16.92Z"/></svg>
                  </span>
                  <span><b>Phone:</b> (703) 459-1079</span>
                </div>
                <div className="footer-contact-row">
                  <span className="footer-contact-icon" aria-label="email">{/* email icon */}
                    <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 6-10 7L2 6"/></svg>
                  </span>
                  <span><b>Email:</b> <a href="mailto:sales@inalabgroup.com" className="footer-contact-link">sales@inalabgroup.com</a></span>
                </div>
                <div className="footer-contact-row"></div>
              </div>
              <div style={{ marginTop: 'auto', width: '100%' }}>
                <a href="/admin" className="footer-link" style={{ display: 'block', textAlign: 'left' }}>Staff Login</a>
                <div className="footer-copyright" style={{ marginTop: '1.2rem', textAlign: 'left' }}>© {new Date().getFullYear()} Inalab Inc. All rights reserved.</div>
              </div>
            </div>
            <div className="footer-right">
              <div
                className="footer-badge-container"
                ref={badgeContainerRef}
                style={{ maxWidth: '750px', padding: '.1rem 2rem' }}
              >
                <img src={process.env.PUBLIC_URL + '/cert_footer.png'} alt="Certifications and Badges" style={{ width: '100%', maxWidth: '1000px', height: 'auto', display: 'block', marginLeft: 'auto' }} />
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

export default App;
